import mixpanel from "mixpanel-browser";
import { MixpanelEventName, MixpanelEventProps } from "../lib/mixpanel/mixpanel-events";

export interface MixpanelConfig {
  token: string;
  debug?: boolean;
  track_pageview?: boolean;
  persistence: "cookie";
  secure_cookie: boolean;
  ip?: boolean;
  property_blacklist?: string[];
  cross_subdomain_cookie?: boolean;
  api_host: string;
}

interface User {
  id: number;
  name: string;
  admin: boolean;
}

class MixpanelService {
  private isInitialized = false;
  private config: MixpanelConfig | null = null;
  private currentUser: User | null = null;
  private currentPage: string | null = null;
  private pageStartTime: number | null = null;

  initialize(config: MixpanelConfig): void {
    if (this.isInitialized) {
      return;
    }

    this.config = config;

    try {
      mixpanel.init(config.token, {
        api_host: config.api_host,
        debug: config.debug || false,
        track_pageview: config.track_pageview || false,
        cross_subdomain_cookie: false,
        secure_cookie: config.secure_cookie,
        ip: config.ip || false,
        property_blacklist: config.property_blacklist || [],
      });

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize Mixpanel:", error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  track<T extends MixpanelEventName>(
    eventName: T,
    properties: Omit<MixpanelEventProps<T>, keyof ReturnType<typeof this.getBaseProperties>>,
  ): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      const enrichedProperties = this.enrichProperties(properties as any);

      if (this.config?.debug) {
        console.log(`track: ${eventName}:`, enrichedProperties);
      }

      mixpanel.track(eventName, enrichedProperties);
    } catch (error) {
      console.error(`Failed to track event: ${eventName}`, error);
    }
  }

  identify(user: User): void {
    if (!this.isInitialized) {
      return;
    }

    if (this.currentUser?.id === user.id) {
      return;
    }

    try {
      this.currentUser = user;

      mixpanel.identify(user.id.toString());
      mixpanel.people.set({
        $name: user.name,
        user_id: user.id,
        user_name: user.name,
        is_admin: user.admin,
        last_login: new Date().toISOString(),
      });

      this.track("User Logged In", {
        user_id: user.id,
        user_name: user.name,
        is_admin: user.admin,
      });
    } catch (error) {
      console.error("Failed to identify user:", error);
    }
  }

  reset(): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      if (this.currentUser) {
        this.track("User Logged Out", {});
      }

      mixpanel.reset();
      this.currentUser = null;
      this.currentPage = null;
      this.pageStartTime = null;
    } catch (error) {
      console.error("Failed to reset Mixpanel:", error);
    }
  }

  trackPageView(pageName: string, pagePath: string, additionalProperties?: any): void {
    if (this.currentPage && this.pageStartTime) {
      const timeOnPage = Date.now() - this.pageStartTime;
      this.track("Page Left", {
        page_name: this.currentPage,
        page_path: window.location.pathname,
        time_on_page: timeOnPage,
      });
    }

    this.currentPage = pageName;
    this.pageStartTime = Date.now();

    this.track("Page Viewed", {
      page_name: pageName,
      page_path: pagePath,
      previous_page: this.currentPage,
      ...additionalProperties,
    });
  }

  trackFeature(
    featureName: string,
    actionType: "click" | "view" | "filter" | "search" | "export" | "download",
    additionalProperties?: any,
  ): void {
    this.track("Feature Used", {
      feature_name: featureName,
      action_type: actionType,
      page_name: this.currentPage || "unknown",
      ...additionalProperties,
    });
  }

  trackFilterChanged(filterType: string, filterValue: unknown, previousValue?: unknown): void {
    this.track("Filter Changed", {
      page_name: this.currentPage || "unknown",
      filter_type: filterType,
      filter_value: filterValue,
      previous_value: previousValue,
    });
  }

  private getBaseProperties() {
    return {
      timestamp: new Date().toISOString(),
      user_id: this.currentUser?.id,
      user_name: this.currentUser?.name,
      is_admin: this.currentUser?.admin,
    };
  }

  private enrichProperties(properties: any): any {
    const enriched = {
      ...this.getBaseProperties(),
      ...properties,
      page_name: properties.page_name || this.currentPage,
      page_path: properties.page_path || window.location.pathname,
    };

    return enriched;
  }
}

export const mixpanelService = new MixpanelService();

export default mixpanelService;
